<template>
  <div
    :class="{
      [ns.e('contain')]: ['picture-card'].includes(props.listType),
    }"
  >
    <upload-list
      v-if="isPictureCard && showFileList"
      :disabled="disabled"
      :list-type="listType"
      :files="uploadFiles"
      :show-draggable="props.showDraggable"
      :show-preview="props.showPreview"
      :show-delete="props.showDelete"
      :handle-preview="onPreview"
      :upload-ref="uploadRef"
      @remove="handleRemove"
      @draggable="handleDraggable"
    >
      <template v-if="$slots.file" #default="{ file }">
        <slot name="file" :file="file" />
      </template>
      <template #append>
        <upload-content
          v-show="(limit && limit > uploadFiles.length) || !limit"
          ref="uploadRef"
          v-bind="uploadContentProps"
        >
          <slot v-if="slots.trigger" name="trigger" />
          <slot v-if="!slots.trigger && slots.default" />
        </upload-content>
      </template>
    </upload-list>

    <upload-content
      v-if="!isPictureCard || (isPictureCard && !showFileList)"
      ref="uploadRef"
      v-bind="uploadContentProps"
    >
      <slot v-if="slots.trigger" name="trigger" />
      <slot v-if="!slots.trigger && slots.default" />
    </upload-content>

    <slot v-if="$slots.trigger" />
    <div
      v-if="$slots.tip"
      :class="ns.b(props.type === 'single' ? 'single-tip' : 'tip')"
    >
      <slot name="tip" />
    </div>
    <upload-list
      v-if="!isPictureCard && showFileList"
      :disabled="disabled"
      :list-type="listType"
      :files="uploadFiles"
      :show-draggable="props.showDraggable"
      :show-preview="props.showPreview"
      :show-delete="props.showDelete"
      :handle-preview="onPreview"
      @remove="handleRemove"
      @draggable="handleDraggable"
    >
      <template v-if="$slots.file" #default="{ file }">
        <slot name="file" :file="file" />
      </template>
    </upload-list>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  onBeforeUnmount,
  provide,
  shallowRef,
  toRef,
  useSlots,
} from 'vue'
import { uploadContextKey } from '@element-plus/tokens'
import { useDisabled, useNamespace } from '@element-plus/hooks'
import UploadList from './upload-list.vue'
import UploadContent from './upload-content.vue'
import { useHandlers } from './use-handlers'
import { uploadProps } from './upload'
import type {
  UploadContentInstance,
  UploadContentProps,
} from './upload-content'

defineOptions({
  name: 'ElUpload',
})

const ns = useNamespace('upload')
const props = defineProps(uploadProps)

const slots = useSlots()
const disabled = useDisabled()

const uploadRef = shallowRef<UploadContentInstance>()
const {
  abort,
  submit,
  clearFiles,
  uploadFiles,
  handleStart,
  handleError,
  handleRemove,
  handleSuccess,
  handleProgress,
  handleDraggable,
} = useHandlers(props, uploadRef)

const isPictureCard = computed(() => props.listType === 'picture-card')

const uploadContentProps = computed<UploadContentProps>(() => ({
  ...props,
  fileList: uploadFiles.value,
  onStart: handleStart,
  onProgress: handleProgress,
  onSuccess: handleSuccess,
  onError: handleError,
  onRemove: handleRemove,
  revokeObjectURL,
}))

const revokeObjectURL = () => {
  uploadFiles.value.forEach(({ url }) => {
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url)
  })
  uploadFiles.value = uploadFiles.value.filter(
    ({ url }) => !url?.startsWith('blob:')
  )
}

onBeforeUnmount(revokeObjectURL)

provide(uploadContextKey, {
  accept: toRef(props, 'accept'),
})

defineExpose({
  /** @description cancel upload request */
  abort,
  /** @description upload the file list manually */
  submit,
  /** @description clear the file list  */
  clearFiles,
  /** @description select the file manually */
  handleStart,
  /** @description remove the file manually */
  handleRemove,
})
</script>
